import React, { useEffect, useState } from "react";
import { Link } from "gatsby";
import { StaticImage } from "gatsby-plugin-image";
import { motion, AnimatePresence } from "framer-motion";
import Magnetic from "./Magnetic";

const EASE = [0.76, 0, 0.24, 1];
const primary = [
  ["Engagement", "/engagement-rings"],
  ["Custom", "/custom-jewelry"],
  ["Process", "/process"],
  ["Estate & Gold", "/estate"],
  ["Watch Repair", "/watch-repair"],
  ["About", "/about"],
  ["Visit", "/contact"],
];

export default function Nav({ path }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);

  useEffect(() => { setOpen(false); }, [path]);

  useEffect(() => {
    let last = 0;
    const onScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 40);
      setHidden(y > 400 && y > last && !open);
      last = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [open]);

  useEffect(() => {
    document.documentElement.classList.toggle("nav-open", open);
    if (window.__lenis) open ? window.__lenis.stop() : window.__lenis.start();
  }, [open]);

  return (
    <>
      <motion.header className={`nav ${scrolled ? "is-scrolled" : ""} ${open ? "is-open" : ""}`} animate={{ y: hidden ? -110 : 0 }} transition={{ duration: 0.6, ease: EASE }}>
        <div className="nav-in container">
          <Link to="/" className="nav-logo" aria-label="Bulow Jewelers home">
            <StaticImage src="../images/logo-ivory.png" alt="" placeholder="none" height={40} layout="fixed" />
          </Link>
          <nav className="nav-links" aria-label="Primary">
            {primary.slice(0, 5).map(([l, to]) => (
              <Link key={to} to={to} className={`nav-link ${path === to ? "is-active" : ""}`}>{l}</Link>
            ))}
          </nav>
          <div className="nav-right">
            <a href="tel:+13038722500" className="nav-phone">303 872 2500</a>
            <Magnetic strength={0.25}>
              <button className="nav-burger" onClick={() => setOpen((v) => !v)} aria-expanded={open} aria-label="Menu">
                <span /><span />
                <em>{open ? "Close" : "Menu"}</em>
              </button>
            </Magnetic>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div className="menu" initial={{ clipPath: "inset(0 0 100% 0)" }} animate={{ clipPath: "inset(0 0 0% 0)" }} exit={{ clipPath: "inset(0 0 100% 0)" }} transition={{ duration: 0.9, ease: EASE }}>
            <div className="menu-in container">
              <ul className="menu-list">
                {primary.map(([l, to], i) => (
                  <motion.li key={to} initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 30, opacity: 0, transition: { duration: 0.3 } }} transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.25 + i * 0.05 }}>
                    <Link to={to} className={path === to ? "is-active" : ""}>
                      <span className="menu-num">0{i + 1}</span>{l}
                    </Link>
                  </motion.li>
                ))}
              </ul>
              <motion.aside className="menu-aside" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6, duration: 0.8 }}>
                <div>
                  <p className="eyebrow no-rule">Visit</p>
                  <p>910 16th Street, Suite 1223<br />University Building, 12th floor<br />Denver, Colorado 80202</p>
                </div>
                <div>
                  <p className="eyebrow no-rule">Hours</p>
                  <p>Mon, Tue, Wed, Fri 9:30 to 5:30<br />Thursday 10 to 7<br />Evenings by appointment</p>
                </div>
                <div>
                  <p className="eyebrow no-rule">Talk to us</p>
                  <p><a href="tel:+13038722500" className="link-line">303 872 2500</a></p>
                </div>
              </motion.aside>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <style dangerouslySetInnerHTML={{ __html: `
        .nav { position: fixed; top: 0; left: 0; right: 0; z-index: 1000; height: var(--nav-h); display: flex; align-items: center; transition: background .6s var(--ease), backdrop-filter .6s; }
        .nav.is-scrolled:not(.is-open) { background: rgba(12,11,9,.72); backdrop-filter: blur(14px); -webkit-backdrop-filter: blur(14px); }
        .nav.is-scrolled:not(.is-open)::after { content: ""; position: absolute; left: 0; right: 0; bottom: 0; height: 1px; background: rgba(201,169,97,.15); }
        .nav-in { display: flex; align-items: center; justify-content: space-between; gap: 32px; }
        .nav-logo { display: block; }
        .nav-links { display: flex; gap: 34px; }
        .nav-link { font-size: .7rem; letter-spacing: .26em; text-transform: uppercase; color: var(--bone); position: relative; padding: 6px 0; }
        .nav-link::after { content: ""; position: absolute; left: 0; bottom: 0; width: 100%; height: 1px; background: var(--gold); transform: scaleX(0); transform-origin: right; transition: transform .5s var(--ease); }
        .nav-link:hover::after, .nav-link.is-active::after { transform: scaleX(1); transform-origin: left; }
        .nav-right { display: flex; align-items: center; gap: 28px; }
        .nav-phone { font-size: .72rem; letter-spacing: .22em; color: var(--gold); }
        .nav-burger { display: inline-flex; align-items: center; gap: 12px; padding: 10px 0; }
        .nav-burger span { display: block; width: 26px; height: 1px; background: var(--ivory); transition: transform .5s var(--ease); }
        .nav-burger span + span { margin-top: 0; }
        .nav-burger span:first-child { transform: translateY(-3px); }
        .nav-burger span:nth-child(2) { transform: translateY(3px); width: 18px; margin-left: -26px; }
        .is-open .nav-burger span:first-child { transform: translateY(0) rotate(45deg); }
        .is-open .nav-burger span:nth-child(2) { width: 26px; transform: translateY(0) rotate(-45deg); }
        .nav-burger em { font-style: normal; font-size: .68rem; letter-spacing: .28em; text-transform: uppercase; margin-left: 12px; }
        .menu { position: fixed; inset: 0; z-index: 900; background: var(--ink-2); overflow: hidden; }
        .menu::before { content: ""; position: absolute; inset: 0; background: radial-gradient(60% 50% at 80% 90%, rgba(201,169,97,.14), transparent 70%); }
        .menu-in { position: relative; height: 100%; padding-top: calc(var(--nav-h) + 4vh); display: grid; grid-template-columns: 1.4fr 1fr; align-items: end; padding-bottom: 6vh; gap: 40px; }
        .menu-list { list-style: none; margin: 0; padding: 0; }
        .menu-list li { overflow: hidden; }
        .menu-list a { display: flex; align-items: baseline; gap: 22px; font-family: var(--serif); font-weight: 300; font-size: clamp(2.6rem, 7.2vh, 5.6rem); line-height: 1.12; color: var(--ivory); transition: color .4s, padding-left .6s var(--ease); }
        .menu-list a:hover, .menu-list a.is-active { color: var(--gold-2); padding-left: 14px; }
        .menu-num { font-family: var(--sans); font-size: .62rem; letter-spacing: .3em; color: var(--gold); }
        .menu-aside { display: grid; gap: 28px; color: var(--bone); font-size: .95rem; line-height: 1.7; }
        .menu-aside .eyebrow { margin-bottom: 8px; }
        @media (max-width: 1100px) { .nav-links { display: none; } }
        @media (max-width: 900px) {
          .nav-phone { display: none; }
          .nav-burger em { display: none; }
          .menu-in { grid-template-columns: 1fr; align-items: start; overflow-y: auto; padding-top: calc(var(--nav-h) + 2vh); }
          .menu-list a { font-size: clamp(2rem, 6vh, 3.2rem); }
          .menu-aside { font-size: .85rem; gap: 18px; padding-bottom: 20px; }
        }
      ` }} />
    </>
  );
}
